import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Newspaper, Search, Sparkles, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import ShareToChat from '../../../components/ui/ShareToChat';
import { searchCompanies } from '../../../data/stockScreenerService';
import PageHeader from '../../../components/layout/PageHeader';
import SectionNav from '../../../components/layout/SectionNav';
import { discoverTabs } from '../../../nav/publicNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SentimentDot({ sentiment }) {
  if (!sentiment) return null;
  const colors = {
    positive: 'bg-emerald-500',
    negative: 'bg-red-500',
    neutral: 'bg-muted-foreground/40',
  };
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${colors[sentiment] || colors.neutral}`} title={sentiment} />
  );
}

export default function NewsPage() {
  const [ticker, setTicker] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [newsData, setNewsData] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(null);
  const [summaries, setSummaries] = useState({});

  const resolveTicker = () => {
    if (ticker) return ticker;
    const parenMatch = searchInput.match(/\(([A-Z0-9._-]+)\)/);
    if (parenMatch?.[1]) return parenMatch[1];
    return searchInput.trim().toUpperCase();
  };

  const handleSearchInputChange = async (value) => {
    setSearchInput(value);
    setTicker('');
    if (!value.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const results = await searchCompanies({ searchQuery: value.trim() });
      setSuggestions(results.slice(0, 8));
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (company) => {
    setTicker(company.symbol);
    setSearchInput(`${company.name} (${company.symbol})`);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const fetchNews = async () => {
    const searchTicker = resolveTicker();
    if (!searchTicker) return;
    setLoading(true);
    setExpandedId(null);
    try {
      const response = await axios.get(`${API}/news/${searchTicker.toUpperCase()}?limit=15`);
      setTicker(searchTicker.toUpperCase());
      setShowSuggestions(false);
      const articles = response.data.data?.news || [];
      setNewsData(articles);
      if (articles.length > 0) setExpandedId(articles[0].id);
    } catch {
      setNewsData([]);
    } finally {
      setLoading(false);
    }
  };

  const summarizeArticle = async (newsItem) => {
    if (summaries[newsItem.id]) return;
    setSummaryLoading(newsItem.id);
    try {
      const response = await axios.post(`${API}/chat`, {
        message: `Summarize this news article concisely: Title: ${newsItem.title}. Content: ${newsItem.full_content || newsItem.summary || ''}`,
      });
      setSummaries((prev) => ({ ...prev, [newsItem.id]: response.data.response || response.data.message }));
    } catch {
      setSummaries((prev) => ({ ...prev, [newsItem.id]: 'Failed to generate summary.' }));
    } finally {
      setSummaryLoading(null);
    }
  };

  return (
    <div className="space-y-4">
      <SectionNav tabs={discoverTabs} />
      <PageHeader
        title="Market News"
        description="Latest news and AI-powered summaries for any stock"
      />

      {/* Search Bar */}
      <div className="flex gap-2 items-center">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search stock (e.g., RELIANCE, TCS, INFY)"
            value={searchInput}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchNews()}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            className="pl-9 bg-secondary/50 border-border text-sm"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full mt-1 w-full z-20 bg-popover border border-border rounded-xl shadow-2xl max-h-64 overflow-y-auto">
              {suggestions.map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleSuggestionSelect(company)}
                  className="w-full text-left px-3 py-2 hover:bg-secondary border-b border-border/50 last:border-b-0 transition-colors"
                >
                  <div className="text-sm font-medium text-foreground">{company.name}</div>
                  <div className="text-xs text-muted-foreground">{company.symbol}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <Button
          onClick={fetchNews}
          disabled={loading || !searchInput.trim()}
          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 text-sm shrink-0"
        >
          <Search className="w-3.5 h-3.5" />
          {loading ? 'Loading...' : 'Get News'}
        </Button>
      </div>

      {/* News Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent mb-3"></div>
          <p className="text-sm text-muted-foreground">Loading news for {ticker}...</p>
        </div>
      ) : newsData.length > 0 ? (
        <div className="space-y-2">
          {newsData.map((news) => {
            const isExpanded = expandedId === news.id;
            return (
              <Card key={news.id} className="border-border/50 hover:border-border transition-colors overflow-hidden">
                <button
                  className="w-full text-left p-4 flex items-start gap-3"
                  onClick={() => setExpandedId(isExpanded ? null : news.id)}
                >
                  <SentimentDot sentiment={news.sentiment} />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm text-foreground leading-snug mb-1">{news.title || 'Untitled'}</h3>
                    {!isExpanded && (
                      <p className="text-xs text-muted-foreground line-clamp-1">{news.summary || ''}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
                      {news.source && <span className="font-medium">{news.source}</span>}
                      {news.date && <span>· {news.date}</span>}
                      {news.sentiment && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 capitalize">
                          {news.sentiment}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-0 space-y-3 border-t border-border/50 animate-slide-up">
                    {/* AI Summary */}
                    {summaries[news.id] ? (
                      <div className="rounded-lg p-3 bg-emerald-500/5 border border-emerald-500/20">
                        <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-1.5">
                          <Sparkles className="w-3.5 h-3.5" /> AI Summary
                        </div>
                        <p className="text-sm text-foreground/80 leading-relaxed">{summaries[news.id]}</p>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); summarizeArticle(news); }}
                        disabled={summaryLoading === news.id}
                        className="gap-1.5 text-xs"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        {summaryLoading === news.id ? 'Summarizing...' : 'Summarize with AI'}
                      </Button>
                    )}

                    {news.summary && (
                      <div className="rounded-lg p-3 bg-secondary/50">
                        <p className="text-sm text-foreground/80 leading-relaxed">{news.summary}</p>
                      </div>
                    )}

                    {news.full_content && (
                      <p className="text-sm text-foreground/70 whitespace-pre-wrap leading-relaxed">{news.full_content}</p>
                    )}

                    <div className="flex items-center gap-3">
                      {news.url && (
                        <a href={news.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 font-medium">
                          Read Full Article <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <ShareToChat
                        messageType="shared_news"
                        payload={{
                          type: 'news',
                          title: news.title || 'News article',
                          summary: news.summary || '',
                          source: news.source || '',
                          url: news.url || '/public/news',
                        }}
                      />
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ) : ticker && !loading ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Newspaper className="w-10 h-10 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">No news found for {ticker}</p>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
            <Newspaper className="w-7 h-7 text-muted-foreground" />
          </div>
          <h3 className="text-base font-semibold text-foreground mb-1">Search for stock news</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            Enter a ticker symbol above to get the latest news and AI-powered summaries
          </p>
        </div>
      )}
    </div>
  );
}
